import React from 'react'
import { createPortal } from 'react-dom'
import { AlertTriangle, X } from 'lucide-react'
import LoadingButton from './LoadingButton'
import { useModalScrollLock } from '../hooks/useModalScrollLock'

interface ConfirmDialogProps {
  isOpen: boolean
  title: string
  message: string
  confirmText?: string
  cancelText?: string
  isDangerous?: boolean
  isLoading?: boolean
  onConfirm: () => void | Promise<void>
  onCancel: () => void
}

export default function ConfirmDialog({
  isOpen,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  isDangerous = false,
  isLoading = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  useModalScrollLock(isOpen)

  if (!isOpen) return null

  return createPortal(
    <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-[9999] p-3 sm:p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full">
        {/* Header */}
        <div className="flex items-start justify-between p-4 sm:p-6 border-b border-gray-200 gap-4">
          <div className="flex items-center gap-3 min-w-0">
            {isDangerous && (
              <div className="shrink-0">
                <AlertTriangle className="text-red-600" size={24} />
              </div>
            )}
            <h2 className={`text-base sm:text-lg font-bold ${isDangerous ? 'text-red-600' : 'text-gray-900'}`}>
              {title}
            </h2>
          </div>
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50 shrink-0"
            aria-label="Close dialog"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 sm:p-6">
          <p className="text-sm sm:text-base text-gray-600">{message}</p>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 sm:p-6 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 rounded-lg transition border border-gray-300 disabled:opacity-50"
          >
            {cancelText}
          </button>
          <LoadingButton
            onClick={onConfirm}
            isLoading={isLoading}
            variant={isDangerous ? 'danger' : 'primary'}
            className="text-sm px-4 py-2"
          >
            {confirmText}
          </LoadingButton>
        </div>
      </div>
    </div>,
    document.body
  )
}
